import type { AnyDoc, CarouselDoc, ID, Project, SplitterDoc, Template, TemplatePreset, ToolId } from '../types';
import { createCarousel, createProject, createSplitter, makeSlot, syncSlots, DEFAULT_CANVAS } from './defaults';
import { isValid } from '../template';

/**
 * Reading a saved document back.
 *
 * None of this touches a storage API: the caller hands over a string and gets
 * a document, so localStorage on the web and a key-value store on a phone are
 * the same three lines around the same migration.
 */

export const DOC_STORAGE_KEY: Record<ToolId, string> = {
  collage: 'nextcollage:project:v1',
  splitter: 'nextcollage:splitter:v1',
  carousel: 'nextcollage:carousel:v1',
};

export const TOOL_KEY = 'nextcollage:tool:v1';
export const TEMPLATES_KEY = 'nextcollage:templates:v1';
export const THEME_KEY = 'nextcollage:theme:v1';
export const LICENSE_KEY = 'nextcollage:license:v1';
export const QUOTA_KEY = 'nextcollage:exports:v1';

export const TOOL_IDS: ToolId[] = ['collage', 'splitter', 'carousel'];

export function isToolId(value: unknown): value is ToolId {
  return value === 'collage' || value === 'splitter' || value === 'carousel';
}

/** Parse user-created layouts without trusting values read from storage. */
export function parseTemplatePresets(raw: string): TemplatePreset[] {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];

    const presets: TemplatePreset[] = [];
    for (const value of parsed) {
      if (!value || typeof value !== 'object') continue;
      const candidate = value as Record<string, unknown>;
      const source = candidate.template;
      if (
        typeof candidate.id !== 'string' ||
        typeof candidate.name !== 'string' ||
        !source ||
        typeof source !== 'object'
      ) continue;

      const input = source as Record<string, unknown>;
      if (
        !Number.isInteger(input.cols) ||
        !Number.isInteger(input.rows) ||
        (input.cols as number) < 1 ||
        (input.rows as number) < 1 ||
        !Array.isArray(input.cells) ||
        input.cells.length < 1
      ) continue;

      const ids = new Set<string>();
      const cells = [];
      let validCells = true;
      for (const value of input.cells) {
        if (!value || typeof value !== 'object') {
          validCells = false;
          break;
        }
        const cell = value as Record<string, unknown>;
        if (
          typeof cell.id !== 'string' ||
          !cell.id ||
          ids.has(cell.id) ||
          !Number.isInteger(cell.c) ||
          !Number.isInteger(cell.r) ||
          !Number.isInteger(cell.cw) ||
          !Number.isInteger(cell.rh) ||
          (cell.cw as number) < 1 ||
          (cell.rh as number) < 1
        ) {
          validCells = false;
          break;
        }
        ids.add(cell.id);
        cells.push({
          id: cell.id,
          c: cell.c as number,
          r: cell.r as number,
          cw: cell.cw as number,
          rh: cell.rh as number,
        });
      }

      const template: Template = {
        cols: input.cols as number,
        rows: input.rows as number,
        cells,
      };
      const name = candidate.name.trim().slice(0, 60);
      if (!validCells || !name || !isValid(template)) continue;
      presets.push({ id: candidate.id, name, template, custom: true });
    }
    return presets;
  } catch {
    return [];
  }
}

/**
 * Every asset id any tool still references.
 *
 * The three documents share one blob store, so pruning against a single
 * document's `assetOrder` would delete the other two tools' photos. Kept pure
 * and exported precisely so that invariant can be unit-tested.
 */
export function unionAssetIds(docs: (AnyDoc | null | undefined)[]): ID[] {
  const out = new Set<ID>();
  for (const doc of docs) {
    if (!doc) continue;
    for (const id of doc.assetOrder ?? []) out.add(id);
  }
  return [...out];
}

/* ── Migration ─────────────────────────────────────────────────────────── */

/** Fill in fields added after a document was saved by an older build. */
export function migrateProject(raw: Project): Project {
  const project: Project = {
    ...createProject(),
    ...raw,
    canvas: { ...DEFAULT_CANVAS, ...raw.canvas },
  };
  if (!project.template || !isValid(project.template)) return createProject();
  project.assetOrder = (project.assetOrder ?? []).filter((id) => project.assets?.[id]);
  syncSlots(project);
  return project;
}

export function migrateSplitter(raw: SplitterDoc): SplitterDoc {
  const base = createSplitter();
  const doc: SplitterDoc = {
    ...base,
    ...raw,
    source: { ...makeSlot(), ...raw.source },
    background: raw.background ?? base.background,
  };
  doc.cols = Math.min(10, Math.max(1, Math.round(doc.cols) || base.cols));
  doc.rows = Math.min(10, Math.max(1, Math.round(doc.rows) || base.rows));
  doc.assets = doc.assets ?? {};
  doc.assetOrder = (doc.assetOrder ?? []).filter((id) => doc.assets[id]);
  return doc;
}

export function migrateCarousel(raw: CarouselDoc): CarouselDoc {
  const base = createCarousel();
  const doc: CarouselDoc = {
    ...base,
    ...raw,
    background: raw.background ?? base.background,
    layers: Array.isArray(raw.layers) ? raw.layers : [],
  };
  doc.slides = Math.min(10, Math.max(2, Math.round(doc.slides) || base.slides));
  doc.assets = doc.assets ?? {};
  doc.assetOrder = (doc.assetOrder ?? []).filter((id) => doc.assets[id]);
  return doc;
}

export const CURRENT_VERSION = 1;

/** Migrate an already-parsed document of a known tool. */
export function migrateDoc(tool: ToolId, raw: AnyDoc): AnyDoc {
  if (tool === 'collage') return migrateProject(raw as Project);
  if (tool === 'splitter') return migrateSplitter(raw as SplitterDoc);
  return migrateCarousel(raw as CarouselDoc);
}

export function parseDoc(tool: ToolId, raw: string): AnyDoc | null {
  try {
    const parsed = JSON.parse(raw) as AnyDoc;
    // A document written by a newer deploy is not something this build can
    // safely half-load — better to fall back to a fresh one.
    if ((parsed.version ?? 1) > CURRENT_VERSION) return null;
    return migrateDoc(tool, parsed);
  } catch {
    return null;
  }
}

/** Forget photos whose blob did not survive, without losing the composition. */
export function reconcileMissing(doc: AnyDoc, gone: Set<ID>) {
  if (!gone.size) return;
  doc.assetOrder = doc.assetOrder.filter((id) => !gone.has(id));
  for (const id of gone) delete doc.assets[id];

  if ('slots' in doc) {
    for (const slot of Object.values(doc.slots)) {
      if (slot.assetId && gone.has(slot.assetId)) slot.assetId = null;
    }
    return;
  }
  if ('source' in doc) {
    if (doc.source.assetId && gone.has(doc.source.assetId)) doc.source.assetId = null;
    return;
  }
  // A layer whose photo is gone keeps its rect and becomes a placeholder —
  // the user's composition survives a storage eviction.
  for (const layer of doc.layers) {
    if (layer.type === 'image' && layer.assetId && gone.has(layer.assetId)) layer.assetId = null;
  }
}
